import { Organization } from "./types/Organization.type";

/** The organization configuration key the KBo User Agreement is stored under. */
export const kboUserAgreementConfigurationKey = "KboUserAgreement";

/** It is recorded as the version plus part of the text's hash, in a 32-character column. */
export const kboUserAgreementMaxVersionLength = 23;

type StoredKboUserAgreement = {
    version?: string;
    title?: string;
    effectiveOn?: string;
    content?: string;
};

const datePattern = /^\d{4}-\d{2}-\d{2}$/;

function hasAnyAgreementField(org: Organization) {
    return [
        org.kboUserAgreementVersion,
        org.kboUserAgreementTitle,
        org.kboUserAgreementEffectiveOn,
        org.kboUserAgreementContent,
    ].some((value) => !!value?.trim());
}

/** Copies the stored agreement onto the organization's form fields. */
export function readKboUserAgreement(org: Organization) {
    const stored = org.organizationConfigurations?.[kboUserAgreementConfigurationKey];
    if (!stored) return;

    try {
        const agreement = JSON.parse(stored) as StoredKboUserAgreement;
        org.kboUserAgreementVersion = agreement.version ?? "";
        org.kboUserAgreementTitle = agreement.title ?? "";
        org.kboUserAgreementEffectiveOn = agreement.effectiveOn?.slice(0, 10) ?? "";
        org.kboUserAgreementContent = agreement.content ?? "";
    } catch {
        // The server ignores an agreement it can't read, so the form starts empty.
        console.error("The organization's KBo User Agreement isn't valid JSON.");
    }
}

/**
 * The value to store, or undefined when every field is blank, which removes the
 * agreement so nobody in the organization is asked to accept one.
 */
export function serializeKboUserAgreement(org: Organization) {
    if (!hasAnyAgreementField(org)) return undefined;

    const agreement: StoredKboUserAgreement = {
        version: org.kboUserAgreementVersion?.trim(),
        title: org.kboUserAgreementTitle?.trim() || undefined,
        effectiveOn: org.kboUserAgreementEffectiveOn,
        content: org.kboUserAgreementContent,
    };
    return JSON.stringify(agreement);
}

export function validateKboUserAgreementVersion(org: Organization) {
    if (!hasAnyAgreementField(org)) return undefined;

    const version = org.kboUserAgreementVersion?.trim();
    if (!version) return "Enter a version.";
    if (version.length > kboUserAgreementMaxVersionLength)
        return `Must be at most ${kboUserAgreementMaxVersionLength} characters.`;
    return undefined;
}

export function validateKboUserAgreementEffectiveOn(org: Organization) {
    if (!hasAnyAgreementField(org)) return undefined;

    return datePattern.test(org.kboUserAgreementEffectiveOn ?? "")
        ? undefined
        : "Enter the date the agreement takes effect.";
}

export function validateKboUserAgreementContent(org: Organization) {
    if (!hasAnyAgreementField(org)) return undefined;

    return org.kboUserAgreementContent?.trim()
        ? undefined
        : "Enter the agreement text.";
}
